import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { Endpoint, CreateEndpointRequest, UpdateEndpointRequest, PaginatedResponse } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';
import { pairCreatedEndpoints, toAppendableRules } from '../utils/exportEndpoint';
import type { EndpointImportItem } from '../utils/exportEndpoint';

interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

interface BulkCreateResult {
  data: Endpoint[];
  errors: Array<{ index: number; error: string }>;
}

interface UseEndpointsReturn {
  endpoints: Endpoint[];
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  fetchEndpoints: (projectId: string, opts?: { limit?: number; offset?: number }) => Promise<void>;
  createEndpoint: (projectId: string, data: CreateEndpointRequest) => Promise<Endpoint>;
  bulkCreateEndpoints: (projectId: string, items: CreateEndpointRequest[]) => Promise<BulkCreateResult>;
  importEndpoints: (projectId: string, items: EndpointImportItem[]) => Promise<void>;
  updateEndpoint: (projectId: string, endpointId: string, data: UpdateEndpointRequest) => Promise<Endpoint>;
  deleteEndpoint: (projectId: string, endpointId: string) => Promise<void>;
}

export function useEndpoints(): UseEndpointsReturn {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEndpoints = useCallback(async (projectId: string, opts?: { limit?: number; offset?: number }) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
      if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
      const qs = params.toString();
      const json = await api.get<PaginatedResponse<Endpoint>>(
        `/api/projects/${projectId}/endpoints${qs ? `?${qs}` : ''}`
      );
      setEndpoints(json.data || []);
      if (json.pagination) {
        setPagination(json.pagination);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch endpoints');
    } finally {
      setLoading(false);
    }
  }, []);

  const createEndpoint = useCallback(async (
    projectId: string,
    data: CreateEndpointRequest
  ): Promise<Endpoint> => {
    try {
      const json = await api.post<{ data: Endpoint }>(
        `/api/projects/${projectId}/endpoints`,
        data
      );
      const newEndpoint = json.data;
      setEndpoints(prev => [...prev, newEndpoint]);
      toast.success('Endpoint created');
      return newEndpoint;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create endpoint';
      toast.error(message);
      throw err;
    }
  }, []);

  const bulkCreateEndpoints = useCallback(async (
    projectId: string,
    items: CreateEndpointRequest[]
  ): Promise<BulkCreateResult> => {
    try {
      const json = await api.post<BulkCreateResult>(
        `/api/projects/${projectId}/endpoints/bulk`,
        { endpoints: items }
      );
      const created = json.data;
      if (created.length > 0) {
        setEndpoints(prev => [...prev, ...created]);
        toast.success(`Imported ${created.length} endpoint${created.length === 1 ? '' : 's'}`);
      }
      // Per-item failures are reported by the caller, which knows each item's path.
      return json;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to import endpoints';
      toast.error(message);
      throw err;
    }
  }, []);

  /**
   * Create the endpoints, then attach each one's exported rules. Rules can only
   * be imported once their endpoint exists, and the server checks the rule
   * limit and Pro-only actions per endpoint, so anything skipped is reported
   * by path rather than lost silently.
   */
  const importEndpoints = useCallback(async (
    projectId: string,
    items: EndpointImportItem[]
  ): Promise<void> => {
    const result = await bulkCreateEndpoints(projectId, items.map(i => i.endpoint));
    for (const e of result.errors) {
      toast.error(`${items[e.index]?.endpoint.path ?? `Endpoint ${e.index + 1}`}: ${e.error}`);
    }

    for (const { item, endpoint } of pairCreatedEndpoints(items, result)) {
      if (!item.rules || item.rules.length === 0) continue;
      try {
        await api.post(
          `/api/projects/${projectId}/endpoints/${endpoint.id}/rules/import`,
          { rules: toAppendableRules(item.rules) }
        );
      } catch (err) {
        const reason = err instanceof ApiError ? err.message : 'Failed to import rules';
        toast.error(`Rules for ${endpoint.path} were not imported: ${reason}`);
      }
    }
  }, [bulkCreateEndpoints]);

  const updateEndpoint = useCallback(async (
    projectId: string,
    endpointId: string,
    data: UpdateEndpointRequest
  ): Promise<Endpoint> => {
    try {
      const json = await api.put<{ data: Endpoint }>(
        `/api/projects/${projectId}/endpoints/${endpointId}`,
        data
      );
      const updatedEndpoint = json.data;
      setEndpoints(prev => prev.map(e => e.id === endpointId ? updatedEndpoint : e));
      toast.success('Endpoint updated');
      return updatedEndpoint;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update endpoint';
      toast.error(message);
      throw err;
    }
  }, []);

  const deleteEndpoint = useCallback(async (
    projectId: string,
    endpointId: string
  ): Promise<void> => {
    try {
      await api.del(`/api/projects/${projectId}/endpoints/${endpointId}`);
      setEndpoints(prev => prev.filter(e => e.id !== endpointId));
      toast.success('Endpoint deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete endpoint';
      toast.error(message);
      throw err;
    }
  }, []);

  return {
    endpoints,
    pagination,
    loading,
    error,
    fetchEndpoints,
    createEndpoint,
    bulkCreateEndpoints,
    importEndpoints,
    updateEndpoint,
    deleteEndpoint,
  };
}
